import React from 'react';
import { motion } from 'framer-motion';
import { CompassIcon, BriefcaseIcon, GraduationCapIcon, CheckCircle2Icon, CalendarIcon, AwardIcon, TargetIcon, UsersIcon, MicIcon, MessageCircleIcon, HeartIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const pathways = [{
  icon: CompassIcon,
  title: 'Muslim Life Fellowship',
  body: 'A 12-month applied journey across identity, family life, professional responsibilities, and community impact — for adults seeking clarity, balance, and growth.',
  to: '#muslim-life-fellowship'
}, {
  icon: BriefcaseIcon,
  title: 'Professional Muslim Leadership Track',
  body: 'For professionals, entrepreneurs, educators, and leaders who want to lead ethically and engage confidently in professional and public spaces.',
  to: '#professional-leadership'
}, {
  icon: GraduationCapIcon,
  title: 'Educator & Mentor Path',
  body: 'Annual certification for educators, mentors, and community workers — covering child development, safeguarding, and ethical teaching.',
  to: '#educator-mentor'
}];
const features = [{
  icon: CalendarIcon,
  title: '12-month structured pathway',
  body: 'Monthly themes, live sessions, and personal reflection prompts.'
}, {
  icon: TargetIcon,
  title: 'Clear outcomes',
  body: 'Each pathway has defined goals you progress toward, month by month.'
}, {
  icon: AwardIcon,
  title: 'Certificate of completion',
  body: 'Recognition for completing the full 12-month journey.'
}];
// Program prices and tuition amounts are temporarily hidden. Set to true to restore them.
const SHOW_PRICES = false;
export function AnnualPrograms() {
  return <>
      <SubpageHero eyebrow="Structured 12-month pathways for Muslim families" title="Annual Programs" paragraphs={['Guided reflection and practical skills for identity, family life, professional responsibilities, and community impact — grounded in Islamic values and adapted to contemporary realities.']} primaryCta={{
      label: 'Explore Programs',
      to: '#programs'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/2oyAijm6fxaEJiR4qbNAKH/AnnualPrograms6.jpg" imageAlt="Muslim woman reviewing a Muslim Life Fellowship annual growth roadmap booklet at her desk" compact masked data-id="element-987" data-mp="3db5a34zlgg1s" />

      {/* Choose your annual pathway */}
      <section id="programs" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-988" data-mp="2btec53c0xhun">
        <Container data-id="element-989" data-mp="26ujsy0a59ra0">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-990" data-mp="26v31wrhth5bw">
            Choose your annual pathway
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-991" data-mp="r3r3qbs991js">
            All annual programs are delivered online and designed as structured
            12-month journeys with clear outcomes and certificates of
            completion.
          </p>
          <div className="mt-8 rounded-2xl bg-white border border-sage-200 px-6 py-4 text-center" data-id="element-992" data-mp="heqxc7bfb3vk">
            <p className="text-ink-500 italic" data-id="element-993" data-mp="154bnba5zv1ec">
              This is not academic study. It is guided reflection, applied
              learning, and personal growth.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-994" data-mp="w2lx27num8i5">
            {pathways.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-995" data-mp="110dkophgn8gj" data-mpi={i}>
                <Link to={p.to} className={`group block h-full rounded-2xl border border-ink-900/5 p-6 shadow-soft transition-shadow hover:shadow-card ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-[#EDF2F7]'][i % 3]}`} data-id="element-996" data-mp="nvst5u1ou2hj">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-997" data-mp="9vrpy7xyaiyw">
                    <p.icon size={18} strokeWidth={1.6} data-id="element-998" data-mp="2zlv2b92mpwbd" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-999" data-mp="2ukyzyzxxei82">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1000" data-mp="1a5iyws7z20wm">
                    {p.body}
                  </p>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1001" data-mp="1j3cxyax4f8nh">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1002" data-mp="1supo8y39tqre">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1003" data-mp="22s3pvrkjcazn">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Muslim Life Fellowship */}
      <section id="muslim-life-fellowship" className="py-16 md:py-24 scroll-mt-24 bg-white/60" data-id="element-1004" data-mp="ch8xofg0fh6k">
        <Container data-id="element-1005" data-mp="1frn33w9g6l4n">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1006" data-mp="1ft7nwu5ymppb">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1007" data-mp="1orjit8dv09nk">
              <div data-id="element-1008" data-mp="1kon1dl022jl7">
                <div className="flex items-center gap-3 mb-3" data-id="element-1009" data-mp="1kskk45iq194k">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1010" data-mp="1yoo8hi6wyn94">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1011" data-mp="28f97h6qi1tsk">
                    Annual Applied Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1012" data-mp="2hfxma3npvy6i">
                  Muslim Life Fellowship
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1013" data-mp="2cfb06qe9bcvp">
                {SHOW_PRICES && <div className="text-right" data-id="element-1014" data-mp="2w134qcohnwa2">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1015" data-mp="312ipe3mr80rx">
                    €1,200{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1016" data-mp="2n1fz1eoq84b7">
                      / year
                    </span>
                  </p>
                </div>}
                <Link to="/waitlist" data-id="element-1017" data-mp="2dcejtqq09qya">
                  <Button variant="primary" data-id="element-1018" data-mp="2dbd7zk1610fi">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1019" data-mp="23eibf6ogda3v">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1020" data-mp="28dl5jw2w9vx2">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1021" data-mp="1uelslloqv5gq">
                  In 12 months, move from uncertainty to clarity — confident in
                  your identity, family life, and work, without compromising
                  your values.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1022" data-mp="29wmriihjbcws">
                  The Muslim Life Fellowship is a year-long applied learning
                  journey designed for Muslim adults living in non-Muslim
                  countries. It supports participants in developing a mature,
                  confident, and balanced approach to modern life.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1023" data-mp="1r4ka34x2jslh">
                  This is not academic study — it is guided reflection, applied
                  learning, and personal growth.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1024" data-mp="1d62nxbmtao5o">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1025" data-mp="1d51cv75sq1ny">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1026" data-mp="3jumjuksuw342">
                    {['Muslim adults living in non-Muslim countries', 'Parents seeking clarity and security', 'Professionals navigating ethical & social challenges', 'Individuals seeking long-term guidance and prosperity'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1027" data-mp="314d5kcjsokw0">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1028" data-mp="2heb92tv426ro">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1029" data-mp="2hd9uwgist45q">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1030" data-mp="2h7w7byqw4uh1">
                    {[{
                    icon: UsersIcon,
                    text: '2 live group sessions / month'
                  }, {
                    icon: CalendarIcon,
                    text: 'Monthly guided reflection'
                  }, {
                    icon: MicIcon,
                    text: 'Guest speakers & practitioners'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Peer discussion groups'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1031" data-mp="2cbxloiwy5qkb">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1032" data-mp="1etxkr2phy4q5">
                          <f.icon size={14} strokeWidth={2} data-id="element-1033" data-mp="1ca0vhmgh8yzz" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1034" data-mp="1c7ofvfy6z0q3">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1035" data-mp="xcs08ho1zb2z">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1036" data-mp="xf4dwims2za8">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1037" data-mp="ojeyqrbxb15a">
                    {['Greater clarity and confidence', 'Practical tools for family and professional life', 'Stronger sense of responsibility and purpose', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1038" data-mp="5lkhjq0n5jbt">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1039" data-mp="3r4jrsarp8rgf" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1040" data-mp="eqtzkpwflxr0">
                <div className="px-7 md:px-10 py-10" data-id="element-1041" data-mp="iorqbx5uorwi">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1042" data-mp="4ywkv967snnj">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1043" data-mp="xmsj4ibfmnmf">
                    {[{
                    q: 'Q1',
                    title: 'Identity & Values',
                    topics: ['Islamic values in everyday life', 'Adab and ethical conduct', 'Applied fiqh for real-life situations', 'Identity without isolation']
                  }, {
                    q: 'Q2',
                    title: 'Family & Relationships',
                    topics: ['Marriage and family responsibilities', 'Parenting in non-Muslim societies', 'Emotional intelligence and communication', 'Healthy boundaries and respect']
                  }, {
                    q: 'Q3',
                    title: 'Professional & Public Life',
                    topics: ['Ethics at work and in business', 'Negotiation and communication', 'Islamic finance foundations (practical)', 'Responsibility and trust']
                  }, {
                    q: 'Q4',
                    title: 'Leadership & Community',
                    topics: ['Representation and leadership', 'Conflict resolution', 'Community responsibility', 'Applied community project']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1044" data-mp="1gduz0l1b7lc3">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1045" data-mp="1kgrpexx3clql">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1046" data-mp="39visf8ir63zk">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1047" data-mp="30xqcq394cubn">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1048" data-mp="2vzf57z6aqvwl">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1049" data-mp="3ont2udth3q7m" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1050" data-mp="g8j6lmc9lf7r">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1051" data-mp="udjith2htvwn">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1052" data-mp="lkg6jc3l3p3l">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1053" data-mp="li3q53dh5msn">
                        €1,200
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1054" data-mp="bl7cykcmschr">
                        / year
                      </span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1055" data-mp="2lkbjyjwj6op">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1056" data-mp="2n57nslv6xoo">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1057" data-mp="6n5uo1n14h7c" />
                        <span data-id="element-1058" data-mp="1x04t42cxem3v">Certificate of completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1059" data-mp="210yh0vqdc0wr">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1060" data-mp="3li7nygj42yf5" />
                        <span data-id="element-1061" data-mp="3lgcz4r4pym5s">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1062" data-mp="3hf1aanto64yn">
                        <HeartIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1063" data-mp="3v9tvo2w8qfd5" />
                        <span data-id="element-1064" data-mp="909hbcxjj8g7">
                          Limited scholarships available through community
                          partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1065" data-mp="i17kjt05nbxq">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1066" data-mp="d3z1lq76n3vf">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Professional Muslim Leadership Track */}
      <section id="professional-leadership" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-[#F7EDE9]/60" data-id="element-1067" data-mp="1xydaijmyecm8">
        <PatternDecor side="left" y="middle" size="md" data-id="element-1068" data-mp="27q9do93wcsxb" />
        <Container data-id="element-1069" data-mp="27ru69t6ni1sj">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1070" data-mp="1b00dynqyth7g">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1071" data-mp="1f23rdhx72vvd">
              <div data-id="element-1072" data-mp="1ozi6ne7dtem3">
                <div className="flex items-center gap-3 mb-3" data-id="element-1073" data-mp="1p01fm5f20snz">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1074" data-mp="1oxfa0bbq4n8e">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1075" data-mp="30ghv1yqlz9au">
                    Annual Professional Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1076" data-mp="jsqrmj0igtx9">
                  Professional Muslim Leadership Track
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1077" data-mp="a373axu147v0">
                {SHOW_PRICES && <div className="text-right" data-id="element-1078" data-mp="61vdosb5nmmt">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1079" data-mp="5yr239vd1om4">
                    €1,800{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1080" data-mp="143nc2q5s8kq">
                      / year
                    </span>
                  </p>
                </div>}
                <Link to="/waitlist" data-id="element-1081" data-mp="3nb1wv0tcre35">
                  <Button variant="primary" data-id="element-1082" data-mp="1qh74ec4m23ju">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1083" data-mp="1livuk1ebft1m">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1084" data-mp="2equ9ampykoov">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1085" data-mp="251u5mq0ih54c">
                  In 12 months, elevate your leadership — combining ethical
                  Islamic principles with modern business and communication
                  skills.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1086" data-mp="1r2ug7g56cm74">
                  This program is designed for Muslim professionals,
                  entrepreneurs, educators, and community leaders who wish to
                  lead ethically, communicate effectively, and engage
                  confidently in professional and public spaces.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1087" data-mp="3gfer163c9h81">
                  It combines Islamic ethical principles with modern leadership
                  and business practices.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1088" data-mp="1367o3brnxi95">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1089" data-mp="1354w10ilt9rn">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1090" data-mp="1by9ml4i68ogs">
                    {['Entrepreneurs & business owners', 'Managers & team leaders', 'Educators & mentors', 'Community coordinators'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1091" data-mp="o8no6j9mid9u">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1092" data-mp="4k5xq4q7ccy7">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1093" data-mp="knkzw8jb86g">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1094" data-mp="ejdhparfz5ua">
                    {[{
                    icon: CalendarIcon,
                    text: 'Monthly intensive sessions'
                  }, {
                    icon: UsersIcon,
                    text: 'Peer leadership circles'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Optional mentoring'
                  }, {
                    icon: MicIcon,
                    text: 'Expert guest practitioners'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1095" data-mp="19t8qk6vvzghq">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1096" data-mp="19rxjny759sme">
                          <f.icon size={14} strokeWidth={2} data-id="element-1097" data-mp="10t3uuu0226je" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1098" data-mp="14tw4hocuadg7">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1099" data-mp="1xjcu6e92rg8q">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1100" data-mp="1jjbrct991yqm">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1101" data-mp="1ogle21597war">
                    {['Advanced leadership skills', 'Ethical business understanding', 'Professional network', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1102" data-mp="sdxsgci2u373">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1103" data-mp="inuxw7vb4h6n" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1104" data-mp="12997yx1b7efw">
                <div className="px-7 md:px-10 py-10" data-id="element-1105" data-mp="xc948srxmnry">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1106" data-mp="xdtui677rkk0">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1107" data-mp="1xjwagyfsjcqz">
                    {[{
                    q: 'Q1',
                    title: 'Leadership & Ethics',
                    topics: ['Leadership as responsibility', 'Integrity and trust', 'Ethical decision-making']
                  }, {
                    q: 'Q2',
                    title: 'Business & Negotiation',
                    topics: ['Working with non-Muslim partners', 'Contracts and partnerships', 'Negotiation strategies']
                  }, {
                    q: 'Q3',
                    title: 'Finance & Responsibility',
                    topics: ['Islamic finance principles (practical)', 'Ethical investment and business models', 'Financial transparency']
                  }, {
                    q: 'Q4',
                    title: 'Community Impact',
                    topics: ['Leadership in community settings', 'Public communication', 'Impact projects']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1108" data-mp="2vw8jytqpemw6">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1109" data-mp="2vxjs15r6mgt3">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1110" data-mp="2zywsf0bi9rsm">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1111" data-mp="3oignbc6qhp92">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1112" data-mp="3ahnt6skcgw6h">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1113" data-mp="30scqbno1wnr1" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1114" data-mp="2vrgkh4k52riz">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1115" data-mp="2mrtgcb00z7m8">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1116" data-mp="2xbrv5pjqs4co">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1117" data-mp="2xcksy10q3jio">
                        €1,800
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1118" data-mp="2obd8omv1kj7l">
                        / year
                      </span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1119" data-mp="2eljz1regme5z">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1120" data-mp="2ejpeie73oo2f">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1121" data-mp="29mywvmfndkew" />
                        <span data-id="element-1122" data-mp="24nm8uyd1i7aq">Certificate of completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1123" data-mp="20l93wc64adf0">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1124" data-mp="1vmfqskuqsofd" />
                        <span data-id="element-1125" data-mp="1voisi259c5zx">Peer leadership network</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1126" data-mp="b3vljfrbpbd9">
                        <TargetIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1127" data-mp="g25ctf9xfrtl" />
                        <span data-id="element-1128" data-mp="p4obroaldkak">Admission by application</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1129" data-mp="3okb8jzimszk0">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1130" data-mp="3sklr0bnm1ifr">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Educator & Mentor Path */}
      <section id="educator-mentor" className="py-16 md:py-24 scroll-mt-24 bg-sand-50/60" data-id="element-1131" data-mp="39tlumpdsd6cg">
        <Container data-id="element-1132" data-mp="2jlrovgkb9kgn">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1133" data-mp="2jl9ivs7ujrvj">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1134" data-mp="2ajs5wipqk6b2">
              <div data-id="element-1135" data-mp="2fhjvh62wrgc3">
                <div className="flex items-center gap-3 mb-3" data-id="element-1136" data-mp="2fh1vpzh1ejzf">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1137" data-mp="20sxi39wucyni">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1138" data-mp="1ruliwjhwssjg">
                    Annual Certification Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1139" data-mp="1hve3zrvj0pu8">
                  Educator & Mentor Path
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1140" data-mp="1mwa9uazfum2a">
                {SHOW_PRICES && <div className="text-right" data-id="element-1141" data-mp="13ai6ut4uuaq1">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1142" data-mp="y9ukikrkmi25">
                    €1,600{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1143" data-mp="1d6v9igidnywx">
                      / year
                    </span>
                  </p>
                </div>}
                <Link to="/waitlist" data-id="element-1144" data-mp="1m6rqrx2ftvhy">
                  <Button variant="primary" data-id="element-1145" data-mp="1m5p461c5ef7u">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1146" data-mp="1h6cfdb1puy2m">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1147" data-mp="1vyxir6sowcx5">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1148" data-mp="24yasm0kq73ie">
                  In 12 months, become a certified educator — equipped to
                  support Muslim children with confidence, ethics, and care.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1149" data-mp="2cxnvwemoflso">
                  A professional certification program for educators, mentors,
                  and community workers who support Muslim children and families
                  in non-Muslim countries.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1150" data-mp="2qqdiola14uqu">
                  The program focuses on child development, safeguarding,
                  ethical teaching, and responsible community work with strong Islamic values.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1151" data-mp="35no1ygswz4x4">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1152" data-mp="35mmhjlq0d62q">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1153" data-mp="1b8bt3edbsdxh">
                    {['Teachers & educators', 'Homeschool parents', 'Youth mentors', 'Community workers'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1154" data-mp="3fq9whur7mrwb">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1155" data-mp="2bdlgpeiyslj">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1156" data-mp="2abxxlgbodmx">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1157" data-mp="2n2ns4ka5rj8e">
                    {[{
                    icon: UsersIcon,
                    text: 'Live interactive sessions'
                  }, {
                    icon: TargetIcon,
                    text: 'Supervised practicum'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Assessments & feedback'
                  }, {
                    icon: HeartIcon,
                    text: 'Peer support groups'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1158" data-mp="xj5pyokcvree">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1159" data-mp="jkxs8efhwm26">
                          <f.icon size={14} strokeWidth={2} data-id="element-1160" data-mp="ohxyapcchota" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1161" data-mp="34928mqqrk4rq">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1162" data-mp="31oe08z5c2ts6">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1163" data-mp="2wqm761syd1me">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1164" data-mp="35s27fgq1zncw">
                    {['Educator certification', 'Eligibility for partner programs', 'Safeguarding expertise', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1165" data-mp="2rsksru6h1qh9">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1166" data-mp="2mzhsz4b3pksm" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1167" data-mp="7zkcy5prqrul">
                <div className="px-7 md:px-10 py-10" data-id="element-1168" data-mp="803qzbcasw3c">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1169" data-mp="cyoi8l9ko5fn">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1170" data-mp="3gfwy4zkqov7q">
                    {[{
                    q: 'Q1',
                    title: 'Child Development',
                    topics: ['Emotional and cognitive development', 'Age-appropriate learning']
                  }, {
                    q: 'Q2',
                    title: 'Teaching Values Gently',
                    topics: ['Islamic values', 'Communication and trust']
                  }, {
                    q: 'Q3',
                    title: 'Safeguarding & Ethics',
                    topics: ['Child protection standards', 'Boundaries and responsibility']
                  }, {
                    q: 'Q4',
                    title: 'Practicum & Assessment',
                    topics: ['Supervised teaching practice', 'Reflection and evaluation']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1171" data-mp="3mwfajt8zrh78">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1172" data-mp="2vq9u0dhrt4mr">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1173" data-mp="2ly46bfyjx9dd">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1174" data-mp="2cyqx8oecamt6">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1175" data-mp="2h2orolspz9fw">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1176" data-mp="2818kso145pgf" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1177" data-mp="1qosb3ocq50y5">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1178" data-mp="20ihfuepfbbad">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1179" data-mp="29h348qhktkj2">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1180" data-mp="29jfm73nc3uw4">
                        €1,600
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1181" data-mp="2jg24pls6u9uz">
                       / year
                      </span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-1182" data-mp="p19ds029279d">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1183" data-mp="p3vhtppxm4mu">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1184" data-mp="k4ivr76vx1lb" />
                        <span data-id="element-1185" data-mp="1l42cs7729rnd">Educator certification</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1186" data-mp="1g4ps9t3nwwny">
                        <TargetIcon size={14} className="text-forest-700" data-id="element-1187" data-mp="1v1q931kbf77n" />
                        <span data-id="element-1188" data-mp="1uyudhjvxwumd">Supervised practicum</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1189" data-mp="241ve5521dtin">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1190" data-mp="2402be0ujntfx">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Program features */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-1191" data-mp="2mq0es3necyd8">
        <PatternDecor side="right" y="top" size="lg" data-id="element-1192" data-mp="2ioon7skyq32e" />
        <Container data-id="element-1193" data-mp="31eto1428b4rt">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1194" data-mp="31flbfrlo9w70">
            How Annual Programs work
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1195" data-mp="2sedqeb862ruv">
            A consistent rhythm of structured monthly content, live sessions,
            and personal practice — designed for busy adults.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1196" data-mp="2iou5l00w8nxd">
            {features.map((f, i) => {
            const tones = ['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'];
            return <motion.div key={f.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-1197" data-mp="29o5ofwg7e7g9" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1198" data-mp="1gip2k0yr92r">
                    <f.icon size={18} strokeWidth={1.6} data-id="element-1199" data-mp="t7ghdmzf4rnp" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1200" data-mp="t5cen8d99yp0">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-1201" data-mp="k6ibez5f6a2h">
                    {f.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Access & Scholarships */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1202" data-mp="34uwkx7x14g1y">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1203" data-mp="2zwlaauywu1io" />
        <Container data-id="element-1204" data-mp="2pur4c7vsnc09">
          <h3 className="font-display text-lg text-ink-900" data-id="element-1205" data-mp="2pval3l9pjucp">
            Access & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1206" data-mp="2utkgnwzcgwyu">
            Some cohorts may include limited scholarship places through
            community partnerships to ensure our programs remain accessible to
            motivated learners.
          </p>
        </Container>
      </section>

      {/* Educational disclaimer */}
      <section className="py-10 md:py-14 bg-white/60" data-id="element-1207" data-mp="38mtmjno330cm">
        <Container data-id="element-1208" data-mp="3di8z8cacuq7g">
          <aside aria-labelledby="annual-programs-disclaimer" className="rounded-2xl border border-[#E0BFB3]/60 bg-[#F7EDE9] px-6 py-5 md:px-8 md:py-6" data-id="element-1209" data-mp="3dhpigywfy7v0">
            <h2 id="annual-programs-disclaimer" className="font-display text-lg text-ink-900" data-id="element-1210" data-mp="3nh80h9ssjjkf">
              Disclaimer
            </h2>
            <p className="mt-3 max-w-5xl text-sm leading-relaxed text-ink-700" data-id="element-1211" data-mp="1t4qiohqq1bn6">
              Our programs provide general, applied educational content.
            </p>
            <ul className="mt-4 max-w-5xl list-disc space-y-3 pl-5 text-sm leading-relaxed text-ink-700 marker:text-ink-900" data-id="element-1212" data-mp="3elznn68w7mx0">
              <li className="pl-1" data-id="element-1213" data-mp="3skftyw2ufbem">
                <strong className="text-ink-900" data-id="element-1214" data-mp="3skpj6hg2dej9">
                  Personal &amp; Family Matters:
                </strong>{' '}
                For specific legal or personal rulings (<em data-id="element-1215" data-mp="2zxv09oywan7w">fatwas</em>)
                regarding marriage (<em data-id="element-1216" data-mp="2vv881mybb0a6">nikah</em>), divorce, or inheritance,
                please consult a qualified Islamic scholar or a recognized
                Sharia council. We are glad to help facilitate a connection to
                trusted scholarly authorities upon request.
              </li>
              <li className="pl-1" data-id="element-1217" data-mp="34u3e8z1cwadh">
                <strong className="text-ink-900" data-id="element-1218" data-mp="2h5k56hahftkc">
                  Business &amp; Finance:
                </strong>{' '}
                Professional, financial, and business-related content is
                strictly educational and practical in nature and does not
                constitute regulated legal, tax, or financial advice.
              </li>
            </ul>
          </aside>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1219" data-mp="iqcwqroiou3v">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1220" data-mp="no4mpg5lq65f" />
        <Container data-id="element-1221" data-mp="1bcnza7vop53b">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-1222" data-mp="1be8tfwe36m0n">
            <div className="max-w-xl" data-id="element-1223" data-mp="1fdrcemmckn0u">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1224" data-mp="1fcomagwumolz">
                Start your Annual Program
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-1225" data-mp="16dbavip5zpym">
                Request information, ask questions, and choose the best pathway
                for your goals.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1226" data-mp="3r4ljhj4cyqo4">
              <Link to="/en/waitlist" data-id="element-1227" data-mp="3r28tqjsczc0i">
                <Button variant="primary" data-id="element-1228" data-mp="372qoctgoyqtl">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-1229" data-mp="330lx21dpu6a4">
                <Button variant="secondary" data-id="element-1230" data-mp="331oms5zay2og">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}